import { useRef, useState } from 'react';

import { UploadingFile } from 'components/v2/input/InputUploadZone';
import { BRANCH_PROFILE_ASSET } from 'constants/enums/asset';
import { getCroppedImg } from 'helpers/getCroppedImg';
import { useUpdateBranchProfileMutation } from 'api/branches/query';
import { useCurrentBranch } from './useCurrentBranch';

export const useBranchAvatar = () => {
  const [openUpload, setOpenUpload] = useState(false);
  const [files, setFiles] = useState<UploadingFile[]>([]);
  const [rawImage, setRawImage] = useState<string>();
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<any>(null);
  const [imgSize, setImgSize] = useState<{ width: number; height: number }>();

  const wrapperRef = useRef<HTMLDivElement>(null);

  const { clientStates } = useCurrentBranch();

  const { mutateAsync, isPending: isLoading } = useUpdateBranchProfileMutation(clientStates?.currentSelectedBranch?.id ?? '');

  const handleDrop = (newFiles: File[]) => {
    if (newFiles.length) {
      processFile(newFiles[0]);
    }
  };

  const processFile = (file: File) => {
    const blobUrl = URL.createObjectURL(file);
    setRawImage(blobUrl);
    setZoom(1);
    setCrop({ x: 0, y: 0 });
  };

  const onCropComplete = (_: any, areaPixels: any) => setCroppedAreaPixels(areaPixels);

  const handleConfirm = async () => {
    if (!rawImage || !croppedAreaPixels || !imgSize) return;

    const file = await getCroppedImg(rawImage, croppedAreaPixels, 0, false, 'avatar.png');

    await mutateAsync({ file, type: BRANCH_PROFILE_ASSET.AVATAR_IMAGE });

    URL.revokeObjectURL(rawImage);
    closeModal();
  };

  const handleCancel = () => {
    if (rawImage) {
      URL.revokeObjectURL(rawImage);
      resetImageState();
    } else {
      closeModal();
    }
  };

  const openModal = () => setOpenUpload(true);
  const closeModal = () => {
    setOpenUpload(false);
    resetImageState();
    wrapperRef.current?.blur();
  };

  const resetImageState = () => {
    setRawImage(undefined);
    setCroppedAreaPixels(null);
    setZoom(1);
    setCrop({ x: 0, y: 0 });
    setFiles([]);
    setImgSize(undefined);
  };

  return {
    clientState: {
      openUpload,
      files,
      rawImage,
      crop,
      zoom,
      croppedAreaPixels,
      imgSize,
      wrapperRef,
    },
    action: {
      setOpenUpload,
      setFiles,
      setRawImage,
      setCrop,
      setZoom,
      setCroppedAreaPixels,
      setImgSize,
      handleDrop,
      onCropComplete,
      handleConfirm,
      handleCancel,
      openModal,
      closeModal,
      resetImageState,
    },
    apiState: { isLoading },
  };
};
